import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import Pagination from '../../components/Pagination/Pagination';
import Loader from '../../components/Loader/Loader';
import UsDatePicker from '../../components/UsDatePicker';
import AttendanceDateRangeFilter from '../../components/AttendanceDateRangeFilter/AttendanceDateRangeFilter';
import { getAuthHeaders } from '../../utils/auth';
import {
  formatUsDateFromIso,
  todayIsoLocal,
  attendancePeriodLabel,
} from '../../utils/usDateFormat';
import {
  defaultAttendanceDateRange,
  defaultCustomAttendanceRange,
  buildAttendanceAllByDateParams,
  canFetchWithAttendanceDateFilter,
  ATTENDANCE_PERIOD_CUSTOM,
  formatAttendanceFilterSummary,
} from '../../utils/attendanceDateRange';
import {
  formatAttendanceMinutesAdmin,
  formatCheckInOutDurationFromRecord,
  formatShiftPolicyMinutes,
} from '../../utils/attendanceMetrics';
import AttendanceShiftName from '../../components/AttendanceShiftName/AttendanceShiftName';
import ColumnSortHeader from '../../components/ColumnSortHeader/ColumnSortHeader';
import ColumnStatusFilterHeader from '../../components/ColumnStatusFilterHeader/ColumnStatusFilterHeader';
import ColumnCategoryFilterHeader from '../../components/ColumnCategoryFilterHeader/ColumnCategoryFilterHeader';
import EmployeeMultiSearchSelect from '../../components/EmployeeMultiSearchSelect/EmployeeMultiSearchSelect';
import { ATTENDANCE_COLUMNS } from '../../utils/attendanceTableSort';

const PAGE_SIZE = 100;
const PERIOD_OPTIONS = [
  { value: 'day', label: 'By Date' },
  { value: 'week', label: 'By Week' },
  { value: 'month', label: 'By Month' },
  { value: ATTENDANCE_PERIOD_CUSTOM, label: 'Custom range' },
];

export default function AllCheckInOut({ userProfile }) {
  const isManagerView = userProfile?.role === 'manager';
  const pageTitle = isManagerView ? 'Team Check In/Out' : 'All Check In/Out';
  const defaultRange = defaultAttendanceDateRange();
  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [periodMode, setPeriodMode] = useState('day');
  const [selectedDate, setSelectedDate] = useState(todayIsoLocal);
  const [selectedMonth, setSelectedMonth] = useState(todayIsoLocal().slice(0, 7));
  const [selectedByName, setSelectedByName] = useState([]);
  const [selectedByNo, setSelectedByNo] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [showLateOnly, setShowLateOnly] = useState(false);
  const [showOvertimeOnly, setShowOvertimeOnly] = useState(false);
  const [showEarlyLeaveOnly, setShowEarlyLeaveOnly] = useState(false);
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [statusFilter, setStatusFilter] = useState([]);
  const [locationFilter, setLocationFilter] = useState([]);
  const [shiftFilter, setShiftFilter] = useState([]);
  const [managerInformedFilter, setManagerInformedFilter] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [shiftOptions, setShiftOptions] = useState([]);
  const fetchAbortRef = useRef(null);
  const fetchRequestIdRef = useRef(0);
  const getStatusLabel = (statusValue) => {
    if (statusValue === 'checked_in') return 'Checked In';
    if (statusValue === 'absent' || statusValue === 'leave') return 'Absent';
    return 'Checked Out';
  };
  const getStatusClass = (statusValue) => {
    if (statusValue === 'checked_in') return 'bg-green-100 text-green-700';
    if (statusValue === 'absent' || statusValue === 'leave') return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  const selectedEmployeeNos = Array.from(
    new Set(
      [...selectedByName, ...selectedByNo]
        .map((item) => item.employee_no)
        .filter(Boolean)
    )
  );
  const selectedEmployeeNosKey = selectedEmployeeNos.join(',');

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedEmployeeNosKey]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const headers = await getAuthHeaders();
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/attendance/filter-options`,
          { headers }
        );
        setLocationOptions(
          (response.data.locations || []).map((location) => ({
            label: location.name,
            value: location.name,
          }))
        );
        setShiftOptions(
          (response.data.shifts || []).map((shift) => ({
            label: shift.name,
            value: shift.name,
          }))
        );
      } catch (err) {
        console.error('Error fetching attendance filter options:', err);
      }
    };

    fetchFilterOptions();
  }, []);

  const anchorWorkDate =
    periodMode === 'month' ? `${selectedMonth}-01` : selectedDate;

  const fetchRecords = useCallback(
    async (
      page,
      rangeStart,
      rangeEnd,
      workDate,
      period,
      employeeNosFilter,
      activeSortBy,
      activeSortOrder,
      activeStatusFilter,
      activeLocationFilter,
      activeShiftFilter,
      activeManagerInformedFilter,
      activeLateOnly,
      activeOvertimeOnly,
      activeEarlyLeaveOnly
    ) => {
      if (period === ATTENDANCE_PERIOD_CUSTOM) {
        if (!canFetchWithAttendanceDateFilter(rangeStart, rangeEnd)) return;
      } else if (!workDate) {
        return;
      }

      if (fetchAbortRef.current) {
        fetchAbortRef.current.abort();
      }
      const controller = new AbortController();
      fetchAbortRef.current = controller;
      const requestId = ++fetchRequestIdRef.current;

      try {
        setLoading(true);
        setError(null);
        const headers = await getAuthHeaders();
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/attendance/all-by-date`,
          {
            headers,
            signal: controller.signal,
            params: {
              ...buildAttendanceAllByDateParams(rangeStart, rangeEnd, workDate, period),
              page,
              page_size: PAGE_SIZE,
              ...(employeeNosFilter.length
                ? { employee_nos: employeeNosFilter.join(',') }
                : {}),
              ...(activeSortBy ? { sort_by: activeSortBy, sort_order: activeSortOrder } : {}),
              ...(activeStatusFilter.length
                ? { status_filter: activeStatusFilter.join(',') }
                : {}),
              ...(activeLocationFilter.length
                ? { location_filter: activeLocationFilter.join(',') }
                : {}),
              ...(activeShiftFilter.length
                ? { shift_filter: activeShiftFilter.join(',') }
                : {}),
              ...(activeManagerInformedFilter.length
                ? { manager_informed_filter: activeManagerInformedFilter.join(',') }
                : {}),
              ...(activeLateOnly ? { late_only: true } : {}),
              ...(activeOvertimeOnly ? { overtime_only: true } : {}),
              ...(activeEarlyLeaveOnly ? { early_leave_only: true } : {}),
            },
          }
        );

        if (requestId !== fetchRequestIdRef.current) {
          return;
        }

        if (response.data.success && Array.isArray(response.data.records)) {
          setRecords(response.data.records);
          setTotalPages(response.data.total_pages || 0);
          setCurrentPage(response.data.page || page);
        } else {
          setRecords([]);
          setTotalPages(0);
        }
      } catch (err) {
        if (
          axios.isCancel?.(err) ||
          err?.code === 'ERR_CANCELED' ||
          err?.name === 'CanceledError' ||
          err?.name === 'AbortError'
        ) {
          return;
        }
        if (requestId !== fetchRequestIdRef.current) {
          return;
        }
        console.error('Error fetching all attendance:', err);
        const detail = err.response?.data?.detail;
        setError(
          typeof detail === 'string'
            ? detail
            : 'Failed to load attendance records. Please try again.'
        );
        setRecords([]);
        setTotalPages(0);
      } finally {
        if (requestId === fetchRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    fetchRecords(
      currentPage,
      startDate,
      endDate,
      anchorWorkDate,
      periodMode,
      selectedEmployeeNos,
      sortBy,
      sortOrder,
      statusFilter,
      locationFilter,
      shiftFilter,
      managerInformedFilter,
      showLateOnly,
      showOvertimeOnly,
      showEarlyLeaveOnly
    );
  }, [
    currentPage,
    startDate,
    endDate,
    anchorWorkDate,
    periodMode,
    selectedEmployeeNosKey,
    sortBy,
    sortOrder,
    statusFilter,
    locationFilter,
    shiftFilter,
    managerInformedFilter,
    showLateOnly,
    showOvertimeOnly,
    showEarlyLeaveOnly,
    fetchRecords,
  ]);

  const handleSortChange = (nextSortBy, nextSortOrder) => {
    setSortBy(nextSortBy || '');
    setSortOrder(nextSortBy ? nextSortOrder : 'asc');
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (nextStatusFilter) => {
    setStatusFilter(nextStatusFilter);
    setCurrentPage(1);
  };

  const handleShowLateOnlyChange = (checked) => {
    setShowLateOnly(checked);
    setCurrentPage(1);
  };

  const handleShowOvertimeOnlyChange = (checked) => {
    setShowOvertimeOnly(checked);
    setCurrentPage(1);
  };

  const handleShowEarlyLeaveOnlyChange = (checked) => {
    setShowEarlyLeaveOnly(checked);
    setCurrentPage(1);
  };

  const handleLocationFilterChange = (nextLocationFilter) => {
    setLocationFilter(nextLocationFilter);
    setCurrentPage(1);
  };

  const handleShiftFilterChange = (nextShiftFilter) => {
    setShiftFilter(nextShiftFilter);
    setCurrentPage(1);
  };

  const handleManagerInformedFilterChange = (nextManagerInformedFilter) => {
    setManagerInformedFilter(nextManagerInformedFilter);
    setCurrentPage(1);
  };

  const getCategoryFilterProps = (column) => {
    if (column.filterParam === 'location_filter') {
      return {
        options: locationOptions,
        selectedValues: locationFilter,
        onChange: handleLocationFilterChange,
        searchable: true,
        searchPlaceholder: 'Search locations...',
        emptyMessage: 'No locations found.',
      };
    }
    if (column.filterParam === 'shift_filter') {
      return {
        options: shiftOptions,
        selectedValues: shiftFilter,
        onChange: handleShiftFilterChange,
        searchable: true,
        searchPlaceholder: 'Search shifts...',
        emptyMessage: 'No shifts found.',
      };
    }
    if (column.filterParam === 'manager_informed_filter') {
      return {
        options: column.staticOptions || [],
        selectedValues: managerInformedFilter,
        onChange: handleManagerInformedFilterChange,
        searchable: false,
      };
    }
    return null;
  };

  const handleDateChange = (iso) => {
    const next = iso || todayIsoLocal();
    setSelectedDate(next);
    setSelectedMonth(next.slice(0, 7));
    setCurrentPage(1);
  };

  const handleMonthChange = (e) => {
    const ym = e.target.value;
    setSelectedMonth(ym);
    if (ym) {
      setSelectedDate(`${ym}-01`);
    }
    setCurrentPage(1);
  };

  const handlePeriodChange = (e) => {
    const next = e.target.value;
    setPeriodMode(next);
    if (next === ATTENDANCE_PERIOD_CUSTOM) {
      const customRange = defaultCustomAttendanceRange();
      setStartDate(customRange.startDate);
      setEndDate(customRange.endDate);
    } else {
      setStartDate('');
      setEndDate('');
      if (next === 'month' && selectedDate) {
        setSelectedMonth(selectedDate.slice(0, 7));
      }
    }
    setCurrentPage(1);
  };

  const handleStartDateChange = (iso) => {
    setStartDate(iso);
    setCurrentPage(1);
  };

  const handleEndDateChange = (iso) => {
    setEndDate(iso);
    setCurrentPage(1);
  };

  const isCustomPeriod = periodMode === ATTENDANCE_PERIOD_CUSTOM;
  const periodSummary = isCustomPeriod
    ? formatAttendanceFilterSummary(startDate, endDate, formatUsDateFromIso)
    : attendancePeriodLabel(periodMode, selectedDate, selectedMonth);
  const openImageModal = (record) => {
    setSelectedRecord(record);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedRecord(null);
  };

  const formatCoord = (val) => {
    if (val == null || val === '') return '-';
    const n = parseFloat(val);
    if (Number.isNaN(n)) return '-';
    if (n % 1 === 0) return n.toString();
    return n.toFixed(6).replace(/\.?0+$/, '');
  };

  const parseExportError = async (err) => {
    const data = err.response?.data;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        const parsed = JSON.parse(text);
        if (typeof parsed.detail === 'string') return parsed.detail;
      } catch (_) {
        /* ignore */
      }
    }
    if (typeof data?.detail === 'string') return data.detail;
    return 'Failed to export attendance. Please try again.';
  };

  const handleExport = async () => {
    if (isCustomPeriod) {
      if (!canFetchWithAttendanceDateFilter(startDate, endDate)) return;
    } else if (!anchorWorkDate) {
      return;
    }
    try {
      setExporting(true);
      setExportError(null);
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/attendance/all-by-date/export`,
        {
          headers,
          params: {
            ...buildAttendanceAllByDateParams(
              startDate,
              endDate,
              anchorWorkDate,
              periodMode
            ),
            ...(selectedEmployeeNos.length
              ? { employee_nos: selectedEmployeeNos.join(',') }
              : {}),
            ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
            ...(statusFilter.length ? { status_filter: statusFilter.join(',') } : {}),
            ...(locationFilter.length ? { location_filter: locationFilter.join(',') } : {}),
            ...(shiftFilter.length ? { shift_filter: shiftFilter.join(',') } : {}),
            ...(managerInformedFilter.length
              ? { manager_informed_filter: managerInformedFilter.join(',') }
              : {}),
          },
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data], {
        type:
          response.headers['content-type'] ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";\n]+)"?/i);
      link.download = match ? match[1].trim() : 'attendance_export.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting attendance:', err);
      setExportError(await parseExportError(err));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-4 max-w-full">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="font-bold text-[22px]">{pageTitle}</h1>
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting || loading}
          className="bg-[#674936] text-white px-4 py-2 rounded-lg hover:bg-[#503220] transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {exporting ? 'Exporting…' : 'Export to Excel'}
        </button>
      </div>

      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div className="w-full sm:max-w-[10rem]">
          <label htmlFor="all-checkin-period" className="block text-sm font-medium text-gray-700 mb-1">
            Period
          </label>
          <select
            id="all-checkin-period"
            value={periodMode}
            onChange={handlePeriodChange}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#674936] bg-white"
          >
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        {!isCustomPeriod && (
          periodMode === 'month' ? (
            <div className="w-full sm:max-w-xs">
              <label htmlFor="all-checkin-month" className="block text-sm font-medium text-gray-700 mb-1">
                Month
              </label>
              <input
                id="all-checkin-month"
                type="month"
                value={selectedMonth}
                onChange={handleMonthChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#674936]"
              />
            </div>
          ) : (
            <div className="w-full sm:max-w-xs">
              <UsDatePicker
                id="all-checkin-date"
                label={periodMode === 'week' ? 'Week (any day in range)' : 'Date'}
                value={selectedDate}
                onChange={handleDateChange}
              />
            </div>
          )
        )}
        {isCustomPeriod && (
          <AttendanceDateRangeFilter
            idPrefix="all-checkin"
            compact
            startDate={startDate}
            endDate={endDate}
            onStartChange={handleStartDateChange}
            onEndChange={handleEndDateChange}
          />
        )}
        <EmployeeMultiSearchSelect
          id="all-checkin-employee-name"
          label="Employee Name"
          placeholder="Type to search by name"
          mode="name"
          selected={selectedByName}
          onChange={setSelectedByName}
        />
        <EmployeeMultiSearchSelect
          id="all-checkin-employee-id"
          label="Employee ID"
          placeholder="Type to search by Emp ID"
          mode="employee_no"
          selected={selectedByNo}
          onChange={setSelectedByNo}
        />
        <p className="text-sm text-gray-600 pb-2 w-full sm:w-auto">
          {periodSummary}
          {selectedEmployeeNos.length > 0 &&
            ` · filtered by ${selectedEmployeeNos.length} employee${
              selectedEmployeeNos.length === 1 ? '' : 's'
            }`}
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 border border-red-200 text-sm">
          {error}
        </div>
      )}

      {exportError && (
        <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 border border-red-200 text-sm">
          {exportError}
        </div>
      )}

      <div className="relative overflow-x-auto border border-gray-200 rounded-lg w-full shadow-sm mx-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm text-left">
          <thead className="bg-[#674936]">
            <tr>
              {ATTENDANCE_COLUMNS.map((column) => (
                <th key={column.sortKey} className="px-4 py-3 relative">
                  {column.sortType === 'status-filter' ? (
                    <ColumnStatusFilterHeader
                      label={column.label}
                      selectedStatuses={statusFilter}
                      onStatusFilterChange={handleStatusFilterChange}
                      showLateOnly={showLateOnly}
                      onShowLateOnlyChange={handleShowLateOnlyChange}
                      showOvertimeOnly={showOvertimeOnly}
                      onShowOvertimeOnlyChange={handleShowOvertimeOnlyChange}
                      showEarlyLeaveOnly={showEarlyLeaveOnly}
                      onShowEarlyLeaveOnlyChange={handleShowEarlyLeaveOnlyChange}
                      variant="dark"
                    />
                  ) : column.sortType === 'category-filter' ? (
                    <ColumnCategoryFilterHeader
                      label={column.label}
                      variant="dark"
                      {...getCategoryFilterProps(column)}
                    />
                  ) : (
                    <ColumnSortHeader
                      label={column.label}
                      sortKey={column.sortKey}
                      sortType={column.sortType}
                      activeSortBy={sortBy}
                      activeSortOrder={sortOrder}
                      onSortChange={handleSortChange}
                      variant="dark"
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={ATTENDANCE_COLUMNS.length} className="text-center py-8 text-gray-500">
                  <Loader />
                </td>
              </tr>
            ) : records.length === 0 ? (
              <tr>
                <td colSpan={ATTENDANCE_COLUMNS.length} className="text-center py-8 text-gray-500">
                  No records found.
                </td>
              </tr>
            ) : (
              records.map((record) => {
                const hasDetails =
                  record.image_url ||
                  record.image_url_checkout ||
                  record.check_in_no_location_reason ||
                  record.check_out_no_location_reason;
                return (
                <tr
                  key={record.id}
                  className={`bg-white hover:bg-gray-50${hasDetails ? ' cursor-pointer' : ''}`}
                  onClick={() => {
                    if (hasDetails) {
                      openImageModal(record);
                    }
                  }}
                >
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {record.employee_name || '-'}
                  </td>
                  <td className="px-4 py-3">{record.employee_no || '-'}</td>
                  <td className="px-4 py-3">{record.location_name || '-'}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {formatCheckInOutDurationFromRecord(record)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {record.day_total_hours_formatted || '-'}
                  </td>
                  <td className="px-4 py-3">
                    {formatUsDateFromIso(record.work_date) || record.work_date || '-'}
                  </td>
                  <td className="px-4 py-3">
                    <AttendanceShiftName
                      name={record.effective_shift_name}
                      isSpecialShift={record.is_special_shift}
                    />
                  </td>
                  <td className="px-4 py-3">
                    {formatShiftPolicyMinutes(record.min_work_minutes)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {record.time_short_formatted ?? '-'}
                  </td>
                  <td className="px-4 py-3">
                    {formatShiftPolicyMinutes(record.break_duration_minutes)}
                  </td>
                  <td className="px-4 py-3">
                    {formatAttendanceMinutesAdmin(
                      record.late_checkin_minutes,
                      record.is_special_shift,
                      'late'
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {formatAttendanceMinutesAdmin(
                      record.early_leave_minutes,
                      record.is_special_shift,
                      'early'
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {formatAttendanceMinutesAdmin(
                      record.overtime_minutes,
                      record.is_special_shift
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        record.check_in_inform_manager || record.check_out_inform_manager
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      <span className="w-2 h-2 mr-1 rounded-full bg-current"></span>
                      {record.check_in_inform_manager || record.check_out_inform_manager ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusClass(
                        record.status
                      )}`}
                    >
                      <span className="w-2 h-2 mr-1 rounded-full bg-current"></span>
                      {getStatusLabel(record.status)}
                    </span>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="p-4 flex justify-center">
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={(newPage) => setCurrentPage(newPage)}
            />
          </div>
        )}
      </div>

      {showImageModal && selectedRecord && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 overflow-y-auto overscroll-contain py-4 px-2 sm:items-center sm:py-6">
          <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 max-w-4xl w-full mx-4 max-h-[min(90vh,calc(100dvh-2rem))] overflow-y-auto overscroll-contain min-w-0 my-auto">
            <div className="flex justify-between items-start gap-3 mb-6 min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 break-words pr-2">
                Attendance Details
              </h2>
              <button
                type="button"
                onClick={closeImageModal}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Employee Name</h3>
                  <p className="text-gray-900">{selectedRecord.employee_name || '-'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Employee Number</h3>
                  <p className="text-gray-900">{selectedRecord.employee_no || '-'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check In Location</h3>
                  <p className="text-gray-900">
                    {selectedRecord.check_in_location_name ||
                      selectedRecord.location_name ||
                      '-'}
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check Out Location</h3>
                  <p className="text-gray-900">{selectedRecord.check_out_location_name || '-'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check In Date</h3>
                  <p className="text-gray-900">
                    {formatUsDateFromIso(
                      selectedRecord.check_in_date || selectedRecord.work_date
                    ) ||
                      selectedRecord.check_in_date ||
                      selectedRecord.work_date ||
                      '-'}
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check Out Date</h3>
                  <p className="text-gray-900">
                    {formatUsDateFromIso(selectedRecord.check_out_date) ||
                      selectedRecord.check_out_date ||
                      '-'}
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check In Time</h3>
                  <p className="text-gray-900">{selectedRecord.check_in_time_str || '-'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check Out Time</h3>
                  <p className="text-gray-900">{selectedRecord.check_out_time_str || '-'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Duration</h3>
                  <p className="text-gray-900">{selectedRecord.total_hours_formatted || '-'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Total Work Time (Day)</h3>
                  <p className="text-gray-900">{selectedRecord.day_total_hours_formatted || '-'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Manager Informed</h3>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      selectedRecord.check_in_inform_manager || selectedRecord.check_out_inform_manager
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    <span className="w-2 h-2 mr-1 rounded-full bg-current"></span>
                    {selectedRecord.check_in_inform_manager || selectedRecord.check_out_inform_manager ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="md:col-span-2">
                  <h3 className="font-semibold text-gray-700 mb-2">Check In Reason</h3>
                  <p className="text-gray-900">{selectedRecord.check_in_no_location_reason || '-'}</p>
                </div>
                <div className="md:col-span-2">
                  <h3 className="font-semibold text-gray-700 mb-2">Check Out Reason</h3>
                  <p className="text-gray-900">{selectedRecord.check_out_no_location_reason || '-'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check-in Latitude</h3>
                  <p className="text-gray-900">{formatCoord(selectedRecord.check_in_lat)}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check-in Longitude</h3>
                  <p className="text-gray-900">{formatCoord(selectedRecord.check_in_lon)}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check-out Latitude</h3>
                  <p className="text-gray-900">{formatCoord(selectedRecord.check_out_lat)}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check-out Longitude</h3>
                  <p className="text-gray-900">{formatCoord(selectedRecord.check_out_lon)}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Status</h3>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusClass(
                      selectedRecord.status
                    )}`}
                  >
                    <span className="w-2 h-2 mr-1 rounded-full bg-current"></span>
                    {getStatusLabel(selectedRecord.status)}
                  </span>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold text-gray-700 mb-4">Selfie Images</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedRecord.image_url && (
                  <div className="text-center">
                    <h4 className="font-medium text-gray-600 mb-2">Check In Selfie</h4>
                    <img
                      src={selectedRecord.image_url}
                      alt="Check in selfie"
                      className="w-full h-48 object-cover rounded-lg shadow-lg mx-auto"
                    />
                  </div>
                )}
                {selectedRecord.image_url_checkout && (
                  <div className="text-center">
                    <h4 className="font-medium text-gray-600 mb-2">Check Out Selfie</h4>
                    <img
                      src={selectedRecord.image_url_checkout}
                      alt="Check out selfie"
                      className="w-full h-48 object-cover rounded-lg shadow-lg mx-auto"
                    />
                  </div>
                )}
                {!selectedRecord.image_url && !selectedRecord.image_url_checkout && (
                  <div className="col-span-2 text-center py-8 text-gray-500">
                    <p>No selfie images available for this record</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-center">
              <button
                type="button"
                onClick={closeImageModal}
                className="bg-[#674936] text-white px-6 py-2 rounded-lg hover:bg-[#503220] transition-colors duration-300 font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
